import { Tooltip } from '@assembly-js/design-system';
import clsx from 'clsx';
import { Trans, useTranslation } from 'react-i18next';

import { BackIcon } from 'src/components/icons';
import { PLAN_PAGE } from 'src/constants';
import { SettingsSearchField } from 'src/features/settings/search';
import { useIsStudioWorkspace } from 'src/hooks/useIsStudioWorkspace';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { useAppSelector } from 'src/hooks/useStore';

import { useSettingsSidebarNavigate } from './useSettingsSidebarNavigate';

const BackToAppButton = () => {
  const { t } = useTranslation();
  const navigate = useSettingsSidebarNavigate();
  const backToPortalDestinationPath = useAppSelector(
    (state) => state.ui.backToPortalDestinationPath,
  );
  const { isTrialPeriodExpired, isCancelledPlan, isPaidPlan } = usePlanStatus();
  const isStudioWorkspace = useIsStudioWorkspace();
  // Same rule as the legacy back toggle: a lapsed Classic workspace has to pick a plan first.
  const disabled =
    !isStudioWorkspace &&
    (isTrialPeriodExpired || isCancelledPlan) &&
    !isPaidPlan;

  const button = (
    <button
      type="button"
      data-testid="settings-sidebar-back-to-app"
      disabled={disabled}
      onClick={() => navigate(backToPortalDestinationPath)}
      className={clsx(
        'flex items-center gap-1.5 rounded border-0 bg-transparent px-1.5 py-1 text-[13px] leading-5 font-medium text-secondary',
        disabled
          ? 'cursor-default opacity-50'
          : 'cursor-pointer hover:bg-gray-150 hover:text-black-heading',
      )}
    >
      <BackIcon style={{ fontSize: 14 }} />
      {t(($) => $.page_settings.sidebar.back_to_app)}
    </button>
  );

  if (!disabled) return button;

  return (
    <Tooltip
      offset={{ x: 16 }}
      position="bottom"
      content={
        <span className="text-[12px] font-medium text-black-heading">
          <Trans
            i18nKey="messages.sidebar_back_disabled"
            components={{
              Button: (
                <button
                  type="button"
                  onClick={() => navigate(PLAN_PAGE.path)}
                  className="cursor-pointer border-0 bg-transparent p-0 text-[12px] font-medium text-black-heading underline"
                />
              ),
            }}
          />
        </span>
      }
    >
      <div>{button}</div>
    </Tooltip>
  );
};

/** Back to app, with the search field pinned underneath. */
export const SettingsSidebarHeader = () => (
  <div className="flex flex-col gap-2">
    <BackToAppButton />
    <SettingsSearchField />
  </div>
);
